/**
 * M2 onboarding: wizard container — step state, step config, render current step with page transitions.
 */

import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { useOnboardingState } from '../shared/useOnboardingState';
import { clearPlanIntent } from '../shared/planIntent';
import OrgSetupStep1 from './OrgSetupStep1';
import OrgSetupStep2 from './OrgSetupStep2';
import StepIndicator from '../activation/StepIndicator';

const SLIDE_DURATION = 0.25;

const slideVariants = {
  enterForward: { opacity: 0, x: 24 },
  enterBack: { opacity: 0, x: -24 },
  center: { opacity: 1, x: 0 },
  exitForward: { opacity: 0, x: -24 },
  exitBack: { opacity: 0, x: 24 },
};

export default function OrganizationSetup({
  clerkUser,
  getAuthToken,
  onComplete,
  organizationActivationRedirectUrl,
  onCancel,
  flowMode = 'initial',
}) {
  const posthog = usePostHog();
  // FB-005: Pass clerkUserId so the hook scopes the draft per-user and clears
  // cross-user data synchronously on load (before any state is returned).
  const { state, update, setStep, reset } = useOnboardingState(clerkUser?.id, flowMode);
  const step = state.step || 1;
  const prevStepRef = useRef(step);
  const direction = step > prevStepRef.current ? 1 : step < prevStepRef.current ? -1 : 0;
  // Read inside the Clerk-seed effect below without adding `state` (or `state.phoneNumber`) to
  // its deps — that would make the effect re-fire on every keystroke in this step's phone field.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Resolve the seed email once, as a stable scalar. Phone-first signups (no
  // primary email) still carry an email on emailAddresses[]; fall back to the
  // first address so the create-org payload — and downstream Stripe account
  // creation, which requires a contact_email — is never left empty. Hoisted out
  // of the effect so the dep is a stable string, not the emailAddresses array
  // reference (which Clerk may hand back fresh each render → effect re-fires).
  const seededEmail =
    clerkUser?.primaryEmailAddress?.emailAddress ??
    clerkUser?.emailAddresses?.[0]?.emailAddress ??
    '';

  // Seed personal info from Clerk (name, email, phone). These are already saved in the user's Clerk account at sign-up; we keep them in onboarding state for the flow and for any backend that needs them.
  //
  // This effect re-fires whenever Clerk hands back a fresh clerkUser reference (e.g. the
  // clerkUser.reload() OrgSetupStep2 issues after provisioning, or the onboarding gate's own
  // remount). Clerk's primaryPhoneNumber can legitimately read empty on that fresh object for
  // phone-first signups where the phone lives as a non-primary identifier, or briefly during a
  // reload. Don't let a blank Clerk value clobber a phone number the user already typed into
  // this step's draft — only seed when the draft doesn't have one yet.
  useEffect(() => {
    if (!clerkUser) return;
    const first = clerkUser.firstName ?? '';
    const last = clerkUser.lastName ?? '';
    const clerkPhone = clerkUser.primaryPhoneNumber?.phoneNumber ?? clerkUser.primaryPhoneNumber?.id ?? '';
    const phone = clerkPhone || stateRef.current.phoneNumber || '';
    update({ firstName: first, lastName: last, email: seededEmail, phoneNumber: phone, clerkUserId: clerkUser.id });
  }, [clerkUser?.id, clerkUser?.firstName, clerkUser?.lastName, seededEmail, clerkUser?.primaryPhoneNumber, update]);

  // Do this before browser paint: a sidebar-launched create flow must never
  // inherit a marketing-page plan intent from a prior signup tab.
  useLayoutEffect(() => {
    if (flowMode === 'create-organization') {
      clearPlanIntent();
    }
  }, [flowMode]);

  useEffect(() => {
    prevStepRef.current = step;
  }, [step]);

  useEffect(() => {
    console.log('[Onboarding] step changed', step);
  }, [step]);

  const handleComplete = (orgData) => {
    // The initial flow deliberately preserves its draft for CreateFundraiserDrawer
    // prefill. A secondary organization flow has no such handoff, so remove its
    // scoped draft (including the idempotency key) once creation succeeds.
    if (flowMode === 'create-organization') {
      reset();
    }
    onComplete?.(orgData);
  };

  const common = {
    state,
    update,
    setStep,
    clerkUser,
    getAuthToken,
    posthog,
  };

  const stepContent =
    step === 1 ? (
      <OrgSetupStep1 {...common} onBack={null} onCancel={onCancel} />
    ) : (
      <OrgSetupStep2
        {...common}
        onBack={() => setStep(1)}
        onCancel={onCancel}
        onComplete={handleComplete}
        organizationActivationRedirectUrl={organizationActivationRedirectUrl}
        step={2}
        totalSteps={2}
      />
    );

  const STEPS = [
    { key: '1', label: 'Your Info' },
    { key: '2', label: 'Verify & Create' },
  ];
  const progressPercent = step === 1 ? 50 : 100;

  return (
    <div className="relative min-h-full overflow-x-hidden overflow-y-visible flex flex-col">
      <StepIndicator
        steps={STEPS}
        currentStep={String(step)}
        progressPercent={progressPercent}
      />

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step}
          initial={direction >= 0 ? 'enterForward' : 'enterBack'}
          animate="center"
          exit={direction >= 0 ? 'exitForward' : 'exitBack'}
          variants={slideVariants}
          transition={{ duration: SLIDE_DURATION, ease: 'easeInOut' }}
          className="w-full flex-1 min-w-0"
        >
          {stepContent}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
